import { ProduktKarticka } from '@/components/druhaJakost/ProduktKarticka';
import { apiFetch } from '@/lib/apiFetch';
import { errMsg } from '@/lib/druhaJakost';
import { useEffect, useState } from 'react';

type Katalog = { name: string; image: string; url: string };
type Navrh = {
  code: string;
  name: string;
  eshop: Katalog | null;
  candidates: { code: string; name: string; image?: string; url?: string }[];
};

/**
 * Starší 2. jakost (založená mimo Tools): napojení na originál podle názvu,
 * aby šlo měnit ceny stejně jako u nových. Originál jde i přepsat ručně.
 */
export function NapojitKarta({
  code,
  onNapojeno,
  onZrusit,
}: {
  code: string;
  onNapojeno: (id: number) => void;
  onZrusit: () => void;
}) {
  const [navrh, setNavrh] = useState<Navrh | null>(null);
  const [error, setError] = useState('');
  const [original, setOriginal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNavrh(null);
    setError('');
    apiFetch<Navrh>(`/api/druha-jakost/napojit/navrh?code=${encodeURIComponent(code)}`)
      .then((n) => {
        setNavrh(n);
        // Jediný kandidát = rovnou předvyplnit.
        if (n.candidates.length === 1 && n.candidates[0]) setOriginal(n.candidates[0].code);
      })
      .catch((e: unknown) => setError(errMsg(e, 'Nepodařilo se načíst 2. jakost.')));
  }, [code]);

  async function napojit() {
    if (!navrh || !original.trim()) return;
    setSaving(true);
    setError('');
    try {
      const res = await apiFetch<{ id: number }>('/api/druha-jakost/napojit', {
        method: 'POST',
        body: { code: navrh.code, original_code: original.trim() },
      });
      onNapojeno(res.id);
    } catch (e: unknown) {
      setError(errMsg(e, 'Napojení selhalo.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-surface rounded-xl shadow-card p-5 mb-8 max-w-2xl">
      {!navrh && !error && <p className="text-sm text-text-muted">Načítám…</p>}
      {navrh && (
        <>
          <p className="text-sm text-text-secondary mb-4">
            Starší 2. jakost, kterou Tools zatím nezná. Po napojení na originál půjde měnit slevu (i
            po velikostech) stejně jako u nových. Ceny se zapíšou jen velikostem, které 2. jakost v
            Shoptetu má.
          </p>
          {/* Vedle sebe: 2. jakost a originál, který systém vybral — ověř, že sedí. */}
          <div className="grid gap-4 sm:grid-cols-2 mb-4">
            <ProduktKarticka
              label="2. jakost"
              code={navrh.code}
              name={navrh.eshop?.name ?? navrh.name}
              image={navrh.eshop?.image}
              url={navrh.eshop?.url}
            />
            {navrh.candidates.length === 1 && navrh.candidates[0] && (
              <ProduktKarticka
                label="Originál (1. jakost) podle názvu"
                code={navrh.candidates[0].code}
                name={navrh.candidates[0].name}
                image={navrh.candidates[0].image}
                url={navrh.candidates[0].url}
              />
            )}
          </div>
          {navrh.candidates.length > 1 && (
            <div className="mb-4">
              <div className="text-sm text-text-muted mb-2">
                Podle názvu sedí víc originálů — vyber ten správný:
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {navrh.candidates.map((c) => (
                  <label
                    key={c.code}
                    className={`flex items-start gap-2 rounded-lg border p-2 cursor-pointer ${
                      original === c.code ? 'border-primary bg-primary-100' : 'border-border'
                    }`}
                  >
                    <input
                      type="radio"
                      name="original"
                      checked={original === c.code}
                      onChange={() => setOriginal(c.code)}
                      className="mt-1"
                    />
                    <ProduktKarticka
                      label="Originál"
                      code={c.code}
                      name={c.name}
                      image={c.image}
                      url={c.url}
                    />
                  </label>
                ))}
              </div>
            </div>
          )}
          {navrh.candidates.length === 0 && (
            <p className="mb-3 text-sm text-warning">
              Originál se podle názvu nenašel — zadej jeho kód.
            </p>
          )}
          <label className="flex items-center gap-2 text-sm text-text" htmlFor="napojit-orig">
            Kód originálu
            <input
              id="napojit-orig"
              type="text"
              value={original}
              onChange={(e) => setOriginal(e.target.value)}
              placeholder="např. SO1233"
              className="w-32 rounded-lg border border-border bg-surface px-3 py-2 font-mono focus:outline-none focus:border-primary"
            />
          </label>
        </>
      )}
      {error && <p className="mt-3 text-sm text-danger">{error}</p>}
      <div className="mt-5 flex gap-3">
        <button
          type="button"
          onClick={() => void napojit()}
          disabled={saving || !navrh || !original.trim()}
          className="rounded-lg bg-action px-5 py-2.5 text-sm font-semibold text-white hover:bg-action-dark disabled:opacity-50 cursor-pointer"
        >
          {saving ? 'Napojuji…' : 'Napojit a upravit ceny'}
        </button>
        <button
          type="button"
          onClick={onZrusit}
          className="rounded-lg border border-border px-5 py-2.5 text-sm text-text-secondary hover:bg-surface-hover cursor-pointer"
        >
          Zrušit
        </button>
      </div>
    </div>
  );
}
